import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatCOP, formatDate, statusLabel, statusColor } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { downloadXlsx } from '@/lib/download';
import { toast } from 'sonner';
import { ADMIN } from '@/constants/testIds';
import { FileDown, Search, Eye, History } from 'lucide-react';
import { Pagination, PAGE_SIZE } from '@/components/Pagination';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const DateField = ({ value, onChange, placeholder, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="outline" className="rounded-none h-9 w-36 justify-start font-normal text-xs" data-testid={testid}>
        {value ? format(parseISO(value), 'dd MMM yyyy', { locale: es }) : <span className="text-slate-400">{placeholder}</span>}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" locale={es} selected={value ? parseISO(value) : undefined}
                onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')} initialFocus />
      {value && <button type="button" className="w-full border-t py-2 text-xs text-slate-600 hover:bg-slate-50" onClick={() => onChange('')}
                        data-testid={`${testid}-clear`}>Quitar fecha</button>}
    </PopoverContent>
  </Popover>
);

const PLAN_LABEL = { basic: 'Básico', pro: 'PRO' };
const ROLE_LABEL = { buyer: 'Comprador', seller: 'Vendedor', admin: 'Admin' };
const ACCOUNT_LABEL = { active: 'Activa', disabled: 'Suspendida', deleted: 'Inactiva' };
const shortDate = (v) => (v ? new Date(v).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : null);
const PLAN_STATE = {
  active: ['Activo', 'bg-emerald-100 text-emerald-800 border-emerald-200'],
  expired: ['Vencido', 'bg-red-100 text-red-800 border-red-200'],
  none: ['Sin plan', 'bg-slate-100 text-slate-600 border-slate-200'],
  no_profile: ['Perfil incompleto', 'bg-amber-100 text-amber-800 border-amber-200'],
};

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [role, setRole] = useState('all');
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('all');
  const [planState, setPlanState] = useState('all');
  const [expFrom, setExpFrom] = useState('');
  const [expTo, setExpTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [incomplete, setIncomplete] = useState(0);
  const [detail, setDetail] = useState(null);

  useSEO({
    title: 'Usuarios | Administración',
    description: 'Listado y gestión de usuarios (compradores, vendedores y administradores).',
    canonical: '/admin/usuarios',
  });

  const fetchData = () => {
    const params = { page, page_size: PAGE_SIZE };
    if (role !== 'all') params.role = role;
    if (q.trim()) params.q = q.trim();
    if (plan !== 'all') params.plan = plan;
    if (planState !== 'all') params.plan_state = planState;
    if (expFrom) params.expires_from = expFrom;
    if (expTo) params.expires_to = expTo;
    api.get('/admin/users/list', { params }).then(({ data }) => {
      setItems(data.items || []); setTotal(data.total || 0); setIncomplete(data.incomplete_seller_profiles || 0);
    }).catch(() => toast.error('No se pudo cargar el listado'));
  };

  useEffect(() => { setPage(1); }, [role, q, plan, planState, expFrom, expTo]);
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, q, plan, planState, expFrom, expTo, page]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = items;

  const openDetail = async (uid) => {
    try {
      const { data } = await api.get(`/admin/users/${uid}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error('No se pudo cargar el detalle');
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === 'active' ? 'disabled' : 'active';
    await api.put(`/admin/users/${u.user_id}/status`, { status: next });
    toast.success(`Usuario ${next === 'active' ? 'activado' : 'desactivado'}`);
    fetchData();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[96rem] px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Usuarios</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                data-testid="admin-users-search"
                placeholder="Buscar por nombre o correo"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none pl-8 w-72"
              />
            </div>
            <Button
              data-testid="export-buyers-btn"
              onClick={() => downloadXlsx('/admin/exports/buyers', 'compradores.xlsx')}
              variant="outline" className="rounded-none"
            >
              <FileDown className="mr-2 h-4 w-4" /> Exportar compradores
            </Button>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="admin-users-role-filter" className="w-48 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="buyer">Compradores</SelectItem>
                <SelectItem value="seller">Vendedores</SelectItem>
                <SelectItem value="admin">Administradores</SelectItem>
              </SelectContent>
            </Select>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="admin-users-plan-filter" className="w-40 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los planes</SelectItem>
                <SelectItem value="basic">Básico</SelectItem>
                <SelectItem value="pro">PRO</SelectItem>
                <SelectItem value="none">Sin plan</SelectItem>
              </SelectContent>
            </Select>
            <Select value={planState} onValueChange={setPlanState}>
              <SelectTrigger data-testid="admin-users-plan-state-filter" className="w-44 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Plan activo</SelectItem>
                <SelectItem value="expired">Plan vencido</SelectItem>
                <SelectItem value="none">Sin plan</SelectItem>
                <SelectItem value="no_profile">Perfil incompleto</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              Vence
              <DateField value={expFrom} onChange={setExpFrom} placeholder="Desde" testid="admin-users-exp-from" />
              –
              <DateField value={expTo} onChange={setExpTo} placeholder="Hasta" testid="admin-users-exp-to" />
            </div>
          </div>
        </div>
        {incomplete > 0 && (
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="admin-users-incomplete-count">
            {incomplete} usuario(s) con rol vendedor sin perfil de vendedor: plan y activación «No disponible».
          </p>
        )}

        <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Ciudad</th>
                <th className="px-4 py-3">Registro</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Estado plan</th>
                <th className="px-4 py-3">Vencimiento</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.map((u) => (
                <tr key={u.user_id} className="row-hover">
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3 uppercase text-xs font-bold tracking-wider">{ROLE_LABEL[u.role] || u.role}</td>
                  <td className="px-4 py-3 text-slate-600">{u.city || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{shortDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-none border ${statusColor[u.status]}`}>{ACCOUNT_LABEL[u.status] || statusLabel[u.status]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs font-bold uppercase" data-testid={`admin-user-plan-${u.user_id}`}>
                    {u.role === 'seller' ? (PLAN_LABEL[u.plan] || (u.plan_state === 'no_profile' ? 'No disponible' : 'Sin plan')) : '—'}
                  </td>
                  <td className="px-4 py-3" data-testid={`admin-user-plan-state-${u.user_id}`}>
                    {u.plan_state ? <Badge className={`rounded-none border ${PLAN_STATE[u.plan_state]?.[1] || ''}`}>{PLAN_STATE[u.plan_state]?.[0] || u.plan_state}</Badge> : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={`admin-user-plan-exp-${u.user_id}`}>
                    {u.plan_expires_at ? shortDate(u.plan_expires_at) : (u.role === 'seller' ? 'No disponible' : '—')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        data-testid={`admin-user-detail-${u.user_id}`}
                        size="sm" variant="outline" className="rounded-none"
                        onClick={() => openDetail(u.user_id)}
                      >
                        <Eye className="mr-1 h-3.5 w-3.5" /> Ver perfil
                      </Button>
                      <Link to={`/admin/usuarios/${u.user_id}`} data-testid={`admin-user-full-detail-${u.user_id}`}
                            className="inline-flex items-center gap-1 text-xs font-bold text-orange-700 hover:underline whitespace-nowrap">
                        <History className="h-3.5 w-3.5" /> Ver detalle
                      </Link>
                      {u.role !== 'admin' && (
                        u.status === 'active' ? (
                          <Button
                            data-testid={ADMIN.disableUserBtn(u.user_id)}
                            variant="outline" size="sm" className="rounded-none"
                            onClick={() => toggleStatus(u)}
                          >
                            Desactivar
                          </Button>
                        ) : (
                          <Button
                            data-testid={ADMIN.activateUserBtn(u.user_id)}
                            size="sm" className="rounded-none bg-emerald-700 hover:bg-emerald-800"
                            onClick={() => toggleStatus(u)}
                          >
                            Activar
                          </Button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {pageItems.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-slate-500">Sin usuarios</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} testIdPrefix="admin-users-pag" />

        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="rounded-none max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="admin-user-detail-dialog">
            {detail && (
              <>
                <DialogHeader>
                  <DialogTitle>{detail.user.name}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="border border-slate-200 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Datos generales</div>
                    <div className="mt-1">{detail.user.email}</div>
                    <div className="text-slate-600">{detail.user.phone || '—'}</div>
                    <div className="text-slate-600">{detail.user.city || '—'}</div>
                    <div className="text-slate-500 text-xs">Rol: <strong className="uppercase">{detail.user.role}</strong></div>
                    <div className="text-slate-500 text-xs">Estado: {statusLabel[detail.user.status] || detail.user.status}</div>
                    <div className="text-slate-500 text-xs">Registro: {formatDate(detail.user.created_at)}</div>
                  </div>
                  <div className="border border-slate-200 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Actividad como comprador</div>
                    <div className="mt-1">Compras: <strong>{detail.buyer.purchases_count}</strong></div>
                    <div>Gasto total: <strong>{formatCOP(detail.buyer.total_spent)}</strong></div>
                    <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Actividad como vendedor</div>
                    <div>Ventas: <strong>{detail.seller.sales_count}</strong></div>
                    <div>Vendido: <strong>{formatCOP(detail.seller.total_sold)}</strong></div>
                    <div>Productos: <strong>{detail.seller.products_count}</strong></div>
                  </div>
                </div>
                {detail.recent_orders.length > 0 && (
                  <div className="mt-3 border border-slate-200 p-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Últimas 10 órdenes</div>
                    <div className="text-xs space-y-1">
                      {detail.recent_orders.map((o) => (
                        <div key={o.order_id} className="flex justify-between border-b border-slate-100 pb-1">
                          <span className="font-mono">{o.order_id}</span>
                          <span className="text-slate-500">{o.status}</span>
                          <span className="font-semibold">{formatCOP(o.total)}</span>
                          <span className="text-slate-400">{formatDate(o.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
